import { ErrorInfo, FC } from 'react';

import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';

import * as Sentry from '@sentry/react';

const getDisplayName = (WrappedComponent: FC) => {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
};

const reportToSentry = (error: Error, info: ErrorInfo) => {
  Sentry.captureException(error, {
    contexts: { react: { componentStack: info.componentStack } },
  });
};

const withErrorHandler = <P extends object>(
  Component: FC<P>,
  Fallback: FC<FallbackProps>,
) => {
  const ComponentWithErrorHandling = (props: P) => {
    const { pathname, search } = useLocation();

    return (
      <ErrorBoundary
        FallbackComponent={Fallback}
        resetKeys={[pathname, search]}
        onError={reportToSentry}
      >
        <Component {...props} />
      </ErrorBoundary>
    );
  };

  ComponentWithErrorHandling.displayName = `WithErrorHandling${getDisplayName(
    Component as FC<unknown>,
  )}`;

  return ComponentWithErrorHandling;
};

export { withErrorHandler };
